/**
 * Redemption Modal (Inlösen)
 *
 * Modal for registering redemption transactions.
 * Redemption (inlösen) is when the company buys back and cancels shares
 * from a shareholder, typically according to a redemption clause
 * (hembudsförbehåll or inlösenklausul).
 */

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Banknote, Info } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import {
  useShareClasses,
  useShareholdersSummary,
  useShareholderHoldings,
  useRegisterRedemption,
} from "@/hooks/use-share-book";
import type { ShareClass, ShareholderWithHoldings, ShareHolding } from "@/lib/api";

interface RedemptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  hasRedemptionClause: boolean;
}

const redemptionSchema = z.object({
  shareholderId: z.string().min(1, "Välj aktieägare"),
  shareClassId: z.string().min(1, "Välj aktieslag"),
  numberFrom: z.coerce.number().min(1, "Aktienummer från krävs"),
  numberTo: z.coerce.number().min(1, "Aktienummer till krävs"),
  pricePerShare: z.coerce.number().min(0, "Pris per aktie kan inte vara negativt"),
  transactionDate: z.string().min(1, "Transaktionsdatum krävs"),
  notes: z.string().optional(),
}).refine((data) => data.numberTo >= data.numberFrom, {
  message: "Aktienummer till måste vara större än eller lika med från",
  path: ["numberTo"],
});

type RedemptionFormData = z.infer<typeof redemptionSchema>;

export function RedemptionModal({
  isOpen,
  onClose,
  companyId,
  hasRedemptionClause,
}: RedemptionModalProps) {
  const { data: shareClasses = [] } = useShareClasses(companyId);
  const { data: shareholders = [] } = useShareholdersSummary(companyId);
  const registerRedemption = useRegisterRedemption();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RedemptionFormData>({
    resolver: zodResolver(redemptionSchema),
    defaultValues: {
      shareholderId: "",
      shareClassId: "",
      numberFrom: undefined,
      numberTo: undefined,
      pricePerShare: 0,
      transactionDate: new Date().toISOString().split("T")[0],
      notes: "",
    },
  });

  const watchShareholderId = watch("shareholderId");
  const watchShareClassId = watch("shareClassId");
  const watchNumberFrom = watch("numberFrom");
  const watchNumberTo = watch("numberTo");
  const watchPricePerShare = watch("pricePerShare");

  // Fetch holdings for selected shareholder
  const { data: holdings = [] } = useShareholderHoldings(
    watchShareholderId || undefined
  );

  // Active shareholders
  const activeShareholders = useMemo(() => {
    return shareholders.filter((s: ShareholderWithHoldings) => s.isActive);
  }, [shareholders]);

  // Filter shareholders that have holdings in selected share class
  const shareholdersWithHoldings = useMemo(() => {
    if (!watchShareClassId) return activeShareholders;
    return activeShareholders.filter((s: ShareholderWithHoldings) =>
      s.holdings.some(
        (h) => h.shareClass.id === watchShareClassId && h.totalShares > 0
      )
    );
  }, [activeShareholders, watchShareClassId]);

  // Get available holdings for selected shareholder in selected share class
  const availableHoldings = useMemo(() => {
    if (!watchShareClassId || !holdings.length) return [];
    return holdings.filter(
      (h: ShareHolding) => h.shareClassId === watchShareClassId
    );
  }, [holdings, watchShareClassId]);

  // Calculate quantity and total amount
  const calculatedQuantity = useMemo(() => {
    if (!watchNumberFrom || !watchNumberTo) return 0;
    return Math.max(0, watchNumberTo - watchNumberFrom + 1);
  }, [watchNumberFrom, watchNumberTo]);

  const totalAmount = useMemo(() => {
    return calculatedQuantity * (watchPricePerShare || 0);
  }, [calculatedQuantity, watchPricePerShare]);

  const onSubmit = async (data: RedemptionFormData) => {
    try {
      await registerRedemption.mutateAsync({
        companyId,
        data: {
          shareholderId: data.shareholderId,
          shareClassId: data.shareClassId,
          quantity: calculatedQuantity,
          numberFrom: data.numberFrom,
          numberTo: data.numberTo,
          pricePerShare: data.pricePerShare,
          transactionDate: data.transactionDate,
          notes: data.notes,
        },
      });
      toast.success(
        `Inlösen registrerad - ${calculatedQuantity.toLocaleString("sv-SE")} aktier`
      );
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte registrera inlösen";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Inlösen av aktier" size="lg">
      <div className="space-y-6">
        {/* Warning if no redemption clause */}
        {!hasRedemptionClause && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="text-amber-600 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <p className="font-medium text-amber-900">
                  Hembudsförbehåll saknas
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Bolagsordningen innehåller inget hembudsförbehåll. Kontrollera att
                  inlösen är tillåten och att alla formkrav är uppfyllda.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Banknote className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-blue-900">Vad är inlösen?</p>
              <p className="text-sm text-blue-700 mt-1">
                Vid inlösen köper bolaget tillbaka aktier från en aktieägare mot
                betalning. Aktierna dras in (makuleras) och aktiekapitalet minskas.
                Kräver beslut av bolagsstämma och registrering hos Bolagsverket.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Share Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieslag *
            </label>
            <select {...register("shareClassId")} className="input">
              <option value="">Välj aktieslag</option>
              {shareClasses.map((sc: ShareClass) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code}) - {sc.totalShares.toLocaleString("sv-SE")}{" "}
                  aktier
                </option>
              ))}
            </select>
            {errors.shareClassId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareClassId.message}
              </p>
            )}
          </div>

          {/* Shareholder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieägare *
            </label>
            <select {...register("shareholderId")} className="input">
              <option value="">Välj aktieägare</option>
              {shareholdersWithHoldings.map((s: ShareholderWithHoldings) => {
                const holding = s.holdings.find(
                  (h) => h.shareClass.id === watchShareClassId
                );
                return (
                  <option key={s.id} value={s.id}>
                    {s.name}{" "}
                    {holding
                      ? `(${holding.totalShares.toLocaleString("sv-SE")} aktier)`
                      : `(${s.totalShares.toLocaleString("sv-SE")} aktier totalt)`}
                  </option>
                );
              })}
            </select>
            {errors.shareholderId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareholderId.message}
              </p>
            )}
          </div>

          {/* Available Holdings Info */}
          {watchShareholderId && watchShareClassId && availableHoldings.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">
                    Tillgängliga aktier för inlösen:
                  </p>
                  <ul className="mt-1 space-y-1">
                    {availableHoldings.map((h: ShareHolding, idx: number) => (
                      <li key={idx} className="text-blue-700">
                        Nr {h.numberFrom} - {h.numberTo} (
                        {h.numberTo - h.numberFrom + 1} st)
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Share Number Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer från *
              </label>
              <input
                type="number"
                {...register("numberFrom")}
                className="input"
                min={1}
              />
              {errors.numberFrom && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberFrom.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer till *
              </label>
              <input
                type="number"
                {...register("numberTo")}
                className="input"
                min={1}
              />
              {errors.numberTo && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberTo.message}
                </p>
              )}
            </div>
          </div>

          {calculatedQuantity > 0 && (
            <p className="text-sm text-gray-600">
              Antal aktier som löses in:{" "}
              <span className="font-medium">
                {calculatedQuantity.toLocaleString("sv-SE")}
              </span>
            </p>
          )}

          {/* Price per Share */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inlösenpris per aktie (kr) *
            </label>
            <input
              type="number"
              step="0.01"
              {...register("pricePerShare")}
              className="input"
              min={0}
            />
            <p className="text-xs text-gray-500 mt-1">
              Belopp som betalas till aktieägaren per aktie
            </p>
            {errors.pricePerShare && (
              <p className="text-sm text-red-600 mt-1">
                {errors.pricePerShare.message}
              </p>
            )}
          </div>

          {/* Total amount preview */}
          {calculatedQuantity > 0 && watchPricePerShare > 0 && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Antal aktier:</span>
                  <span className="font-medium text-gray-900">
                    {calculatedQuantity.toLocaleString("sv-SE")} st
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pris per aktie:</span>
                  <span className="font-medium text-gray-900">
                    {watchPricePerShare.toLocaleString("sv-SE")} kr
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-medium text-gray-700">Total utbetalning:</span>
                  <span className="font-bold text-gray-900">
                    {totalAmount.toLocaleString("sv-SE")} kr
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaktionsdatum *
            </label>
            <input
              type="date"
              {...register("transactionDate")}
              className="input"
            />
            {errors.transactionDate && (
              <p className="text-sm text-red-600 mt-1">
                {errors.transactionDate.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteckningar
            </label>
            <textarea
              {...register("notes")}
              className="input min-h-[80px]"
              placeholder="Valfria anteckningar om inlösen..."
            />
          </div>

          {/* Legal info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Krav för inlösen:</strong> Beslut av bolagsstämma,
              registrering hos Bolagsverket, och i vissa fall kallelse på okända
              borgenärer. Aktiekapitalet minskas med aktiens andel av
              aktiekapitalet.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || calculatedQuantity === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Registrerar...
                </>
              ) : (
                "Registrera inlösen"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
